import React, { useState } from "react";

interface ImageUploaderProps {
  onImageChange: (image: string | null) => void;
  setStep: React.Dispatch<React.SetStateAction<"select" | "crop" | "view">>;
  // Szablony z kilkoma zdjęciami: każdy uploader potrzebuje własnego id inputa i opisu
  inputId?: string;
  label?: string;
}

// Funkcja pomocnicza do zmiany rozmiaru
const resizeImage = (base64Str: string, maxWidth = 2000, maxHeight = 2000): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = base64Str;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      // Logika skalowania z zachowaniem proporcji
      if (width > height) {
        if (width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width *= maxHeight / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Wygładzanie obrazu
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
      }
      
      // Zwracamy jako JPEG (lżejszy niż PNG) z wysoką jakością
      resolve(canvas.toDataURL('image/jpeg', 0.92));
    };
  });
};

const ImageUploader: React.FC<ImageUploaderProps> = ({
  onImageChange,
  setStep,
  inputId = "file-upload",
  label = "Załaduj zdjęcie",
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // Dodatkowy stan ładowania

  const baseStyle: React.CSSProperties = {
    backgroundColor: '#FFFFFF',
    border: '0',
    borderRadius: '0.5rem',
    boxSizing: 'border-box',
    color: '#111827',
    fontFamily: '"Inter var", ui-sans-serif, system-ui, -apple-system, system-ui, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
    fontSize: '0.875rem',
    fontWeight: '600',
    lineHeight: '1.25rem',
    padding: '0.75rem 1rem',
    textAlign: 'center',
    textDecoration: 'none',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    cursor: isProcessing ? 'wait' : 'pointer',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    touchAction: 'manipulation',
    transition: 'all 0.3s ease-in-out',
    display: 'flex',
    flexDirection: 'column',
    width: '400px',
    opacity: isProcessing ? 0.7 : 1
  };

  const hoverStyle: React.CSSProperties = {
    backgroundColor: 'rgb(249, 250, 251)',
    borderRadius: '1rem'
  };

  const combinedStyle: React.CSSProperties = {
    ...baseStyle,
    ...(isHovered ? hoverStyle : {}),
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      setIsProcessing(true); // Zaczynamy proces
      const reader = new FileReader();
      
      reader.onload = async () => {
        try {
          const originalBase64 = reader.result as string;
          // Tutaj dzieje się magia resize'u
          const resizedBase64 = await resizeImage(originalBase64);
          
          onImageChange(resizedBase64);
          setStep("crop");
        } catch (error) {
          console.error("Błąd podczas optymalizacji zdjęcia:", error);
          alert("Wystąpił problem z przetworzeniem zdjęcia.");
        } finally {
          setIsProcessing(false);
        }
      };

      reader.onerror = () => {
        setIsProcessing(false);
        alert("Błąd podczas odczytu pliku.");
      };

      reader.readAsDataURL(file);
    } else {
      onImageChange(null);
      setStep("select");
    }
  };

  return (
    <>
      <label
        htmlFor={inputId}
        className="custom-file-upload"
        style={combinedStyle}
        onMouseEnter={() => !isProcessing && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {isProcessing ? "Przetwarzanie..." : label}
        {!isProcessing && (
          <small style={{ fontWeight: 400, marginTop: '4px', fontSize: '0.75rem', color: '#6b7280' }}>
            (uwaga: ostateczny wygląd pokaże się po wykadrowaniu)
          </small>
        )}
      </label>
      <input
        id={inputId}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        disabled={isProcessing}
        style={{ display: "none" }}
      />
    </>
  );
};

export default ImageUploader;